import React, { useEffect, useCallback, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Typography, Grid } from '@material-ui/core'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '@refera/ui-web'
import {
  getCategoryList,
  deleteCategory,
  updateCategory,
  getCategoryById,
  postCategory,
  getServiceList,
} from '_/modules/service-orders/actions'
import Loading from '_components/loading'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'

import useStyles from './styles'
import DynamicTreeView from '_/components/treeview'
import CategoryEdit from './category-edit'
import CategoryCreate from './category-create'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import useRolePermission from '_/hooks/use-role-permission'

const CategoryScreen = () => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()

  const { checkIfUserDoesNotPermission } = useRolePermission()

  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [category, setCategory] = useState()
  const [categoryList, setCategoryList] = useState()
  const [services, setServicesList] = useState()

  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {
      name: category?.name || '',
      allowsNoAttachments: category?.allowsNoAttachments || false,
      allowsTheDistance: category?.allowsTheDistance || false,
      allowsBypass: category?.allowsBypass || true,
      budgeteerMeets: category?.budgeteerMeets || false,
      isActive: category?.isActive || false,
      services: category?.services || [],
    },
    mode: 'all',
  })

  const {
    reset,
    watch,
    formState: { isDirty },
  } = methods

  const data = watch()

  const message = {
    title: 'Você tem certeza que deseja executar esta ação?',
    error:
      'Você não pode excluir esta opção porque ela tem itens filhos ou já foi utilizada. Tente desativá-la.',
    duplicationError: 'Conteúdo já existente.',
    success: 'Ação executada com sucesso.',
  }

  const openDeleteModal = () => {
    if (checkIfUserDoesNotPermission(PERMISSIONS.CATEGORIES, PERMISSIONS_ACTIONS.DELETE)) {
      return
    }

    showDialog({
      type: 'warning',
      subject: message.title,
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onApprove: () => {
        closeDialog()
        handleDelete()
      },
      onCancel: closeDialog,
    })
  }

  const handleSelection = useCallback(
    e => {
      setIsCreating(false)
      dispatch(getCategoryById(e?.id)).then(res => {
        setCategory(() => (res.count ? {} : res))
      })
    },
    [dispatch, category]
  )

  const handleCreate = () => {
    if (checkIfUserDoesNotPermission(PERMISSIONS.CATEGORIES, PERMISSIONS_ACTIONS.ADD)) {
      return
    }

    setIsCreating(true)

    if (!category) {
      setCategory({})
    }

    reset({
      name: '',
      isActive: false,
      allowsNoAttachments: false,
      allowsTheDistance: false,
      budgeteerMeets: false,
      services: [],
    })
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await dispatch(deleteCategory(category?.id))
      setIsUpdating(true)
      showToast({
        message: message.success,
        type: 'success',
      })
    } catch (error) {
      showToast({
        message: error?.error_message || message.error,
        type: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleUpdate = async payload => {
    if (
      checkIfUserDoesNotPermission(PERMISSIONS.CATEGORIES, [
        PERMISSIONS_ACTIONS.ADD,
        PERMISSIONS_ACTIONS.EDIT,
      ])
    ) {
      return
    }

    const serviceIDArray = payload?.services?.map(item => item.id)

    const request = {
      ...payload,
      noticesToProvider: payload.noticesToProvider?.html ?? null,
      noticesToProviderJson: payload.noticesToProvider?.json ?? null,
      servicesQtExample: payload.servicesQtExample?.html ?? null,
      servicesQtExampleJson: payload.servicesQtExample?.json ?? null,
      serviceDescriptionMandatoryInfo: payload.serviceDescriptionMandatoryInfo?.html ?? null,
      serviceDescriptionMandatoryInfoJson: payload.serviceDescriptionMandatoryInfo?.json ?? null,
      serviceDescriptionDesiredInfo: payload.serviceDescriptionDesiredInfo?.html ?? null,
      serviceDescriptionDesiredInfoJson: payload.serviceDescriptionDesiredInfo?.json ?? null,
    }
    request.services = serviceIDArray

    await Promise.resolve(dispatch(updateCategory(category?.id, request)))
      .then(() => {
        setIsUpdating(true)
        showToast({
          message: message.success,
          type: 'success',
        })
      })
      .catch(error => {
        showToast({
          message: error?.error_message ?? message.error,
          type: 'error',
        })
      })
  }

  const handlePost = async payload => {
    if (checkIfUserDoesNotPermission(PERMISSIONS.CATEGORIES, PERMISSIONS_ACTIONS.ADD)) {
      return
    }

    const serviceIDArray = payload?.services.map(item => item.id)

    const request = {
      ...payload,
      noticesToProvider: payload.noticesToProvider?.html ?? null,
      noticesToProviderJson: payload.noticesToProvider?.json ?? null,
      servicesQtExample: payload.servicesQtExample?.html ?? null,
      servicesQtExampleJson: payload.servicesQtExample?.json ?? null,
      serviceDescriptionMandatoryInfo: payload.serviceDescriptionMandatoryInfo?.html ?? null,
      serviceDescriptionMandatoryInfoJson: payload.serviceDescriptionMandatoryInfo?.json ?? null,
      serviceDescriptionDesiredInfo: payload.serviceDescriptionDesiredInfo?.html ?? null,
      serviceDescriptionDesiredInfoJson: payload.serviceDescriptionDesiredInfo?.json ?? null,
    }
    request.services = serviceIDArray

    await Promise.resolve(dispatch(postCategory({ ...request, parent: category.id })))
      .then(() => {
        setIsUpdating(true)
        showToast({
          message: message.success,
          type: 'success',
        })
      })
      .catch(error =>
        showToast({
          message: error?.error_message ?? message.error,
          type: 'error',
        })
      )
  }

  useEffect(() => {
    dispatch(getCategoryList()).then(res => setCategoryList(() => res))
    dispatch(getServiceList()).then(res => setServicesList(res))
  }, [])

  useEffect(() => {
    // UPDATES TREE VIEW AFTER CATEGORIES ARE SAVED/DELETED
    if (isUpdating) {
      dispatch(getCategoryList()).then(res => {
        setIsUpdating(() => false)
        setIsCreating(() => false)
        setCategoryList(res)
        setCategory()
      })
    }
  }, [isUpdating])

  const handleCancel = useCallback(() => {
    setCategory(null)
    setIsCreating(false)
  }, [])

  const handleCancelRequest = useCallback(() => {
    if (isDirty) {
      showDialog({
        type: 'warning',
        subject: 'Cancelar alterações?',
        description: 'Você tem certeza que deseja fechar a janela sem salvar o registro?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          handleCancel()
          closeDialog()
        },
        onCancel: closeDialog,
      })
    } else {
      handleCancel()
    }
  }, [isDirty, handleCancel, showDialog, closeDialog])

  const handleSave = () => {
    if (isCreating) {
      methods.handleSubmit(handlePost)()
    } else if (category) {
      methods.handleSubmit(handleUpdate)()
    }
  }

  const categoryForms = useMemo(() => {
    if (isCreating && category) {
      return <CategoryCreate data={data} services={services} handlePost={handlePost} />
    }

    if (!isCreating && category) {
      return (
        <CategoryEdit
          data={data}
          category={category}
          services={services}
          handleUpdate={handleUpdate}
          onDelete={openDeleteModal}
          isDeleting={isDeleting}
        />
      )
    }

    return (
      <Grid className={styles.selectWrapper}>
        <Typography className={styles.selectTitle}>Selecione uma categoria.</Typography>
      </Grid>
    )
  }, [category, isCreating, categoryList, data, isDeleting])

  if (!categoryList) {
    return <Loading />
  }

  return (
    <Grid className={styles.container}>
      <Grid className={styles.header}>
        <Typography variant="h4" component="h1" className={styles.title}>
          Categorias
        </Typography>
        <Grid className={styles.buttonWrapper}>
          {(isCreating || category) && (
            <>
              <Button onClick={handleCancelRequest} variant="secondary" color="red">
                Cancelar
              </Button>
              <Button onClick={handleSave}>Salvar</Button>
            </>
          )}
          <Button onClick={() => handleCreate()}>Novo</Button>
        </Grid>
      </Grid>

      <Grid className={styles.main}>
        <Grid className={styles.treeWrapper}>
          <DynamicTreeView
            data={categoryList}
            handleClick={handleSelection}
            setState={setCategory}
          />
        </Grid>
        <FormProvider {...methods}>
          <Grid className={styles.displayWrapper}>{categoryForms}</Grid>
        </FormProvider>
      </Grid>
    </Grid>
  )
}

export default CategoryScreen
